// @flow
import React from 'react';
import gql from 'graphql-tag';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {SVGIcon} from '@homeaway/react-svg';
import HitInfoMobile from './HitInfoMobile';
import HitInfoDesktop from './HitInfoDesktop';
import {media} from '../../utils/breakpoints';
import {injectIntl} from 'react-intl';
import {isDateless} from '../../utils/searchParamsUtils';
import {HA_SERP_FREE_CANCELLATION_V2} from '../../abacus/experiments';
import {fragments as geoDistanceFragments} from '../GeoDistance/GeoDistance';
import {fragments as priceFragments} from '../Price/Price';
import {fragments as detailsFragments} from '../Details/Details';
import {fragments as ratingFragments} from '../Rating/Rating';

import type {Element} from 'react';
import type {IntlShape} from 'react-intl';
import type {ListingShape as DetailsListingShape} from '../Details/Details';
import type {Shape as GeoDistanceShape} from '../GeoDistance/GeoDistance';
import type {
    SERPHitShape as PriceSERPHitShape,
    ListingShape as PriceListingShape
} from '../Price/Price';
import type {ListingShape as RatingListingShape} from '../Rating/Rating';
import type {State as ReduxState} from '../../reducers/index';

import {PP_ACCEPTED, EXCLUSIVITY} from '../../constants/badges';

import './HitInfo.less';

export const fragments = {
    SERPHit: gql`
        fragment HitInfoSERPHit on SERPHit {
            ...PriceSERPHit
            ...GeoDistanceSERPHit
            freeCancellationUntil
            urgencyMessage {
                icon
                message
            }
        }
        ${geoDistanceFragments.SERPHit}
        ${priceFragments.SERPHit}
    `,
    listing: gql`
        fragment HitInfoListing on Listing {
            ...DetailsListing
            ...PriceListing
            ...RatingListing
            detailPageUrl
            instantBookable
            minStayRange {
                minStayHigh
                minStayLow
            }
            listingId
            partnerBadges {
                id
                helpText
                name
            }
            propertyId
            propertyType
            propertyMetadata {
                headline
            }
            reviewBadges {
                name
            }
        }
        ${detailsFragments.listing}
        ${priceFragments.listing}
        ${ratingFragments.listing}
    `
};

export type HitBadge = string;
type InstantBookWrapperProp = {
    title?: string,
    children: Element<typeof SVGIcon>
};

export type Badge = {
    id: string,
    name: string,
    helpText?: string
};

export type ListingShape = DetailsListingShape &
    PriceListingShape &
    RatingListingShape & {
        detailPageUrl: string,
        instantBookable: boolean,
        listingId: string,
        minStayRange: {
            minStayHigh: number,
            minStayLow: number
        },
        partnerBadges: ?(Badge[]),
        propertyId: string,
        propertyMetadata: {
            headline: string
        },
        reviewBadges: ?(Badge[])
    };

export type SERPHitShape = PriceSERPHitShape &
    GeoDistanceShape & {
        freeCancellationUntil?: string,
        urgencyMessage?: {
            icon: string,
            message: string
        },
        fullLocale: string
    };

type StateProps = {
    brand: string,
    isDatelessSearch: boolean,
    isHovered: boolean,
    isDesktop: boolean,
    isMetric: boolean,
    fullLocale: string,
    showDesktopHits: boolean
};

type IntlProps = {
    intl: IntlShape
};

export type OwnProps = {
    hit: SERPHitShape,
    hitIndex?: number,
    isFreeCancellationOff: boolean,
    isFreeCancellationV2: boolean,
    listing: ListingShape
} & IntlProps;

export type Props = OwnProps & StateProps;

export const InstantBookingWrapper = ({
    title,
    children
}: InstantBookWrapperProp) => (
    <span
        className="InstantBook__icon"
        aria-hidden="true"
        title={title ? title : null}
    >
        {children}
    </span>
);

export function HitInfo(props: Props) {
    const {
        hit: {geoDistance},
        intl: {formatMessage},
        listing,
        showDesktopHits
    } = props;

    const instantBookableTitle = formatMessage({
        id: 'instantConfirmationSecondaryLabel',
        defaultMessage: 'Book without waiting for owner approval'
    });

    // Hit Badges
    let partnerBadges = listing.partnerBadges || [];
    let premierPartnerBadge = partnerBadges.find((e) => e.id === PP_ACCEPTED);
    let exclusivityBadge = partnerBadges.find((e) => e.id === EXCLUSIVITY);

    const propsToPass = {
        ...props,
        instantBookableTitle,
        premierPartnerBadge,
        exclusivityBadge,
        geoDistance
    };

    if (showDesktopHits) {
        return <HitInfoDesktop {...propsToPass} />;
    }
    return (
        <HitInfoMobile
            {...{
                ...propsToPass
            }}
        />
    );
}

const mapStateToProps = (
    {
        searchParams: {params},
        map: {listingId: hoveredListingId},
        system: {brand, isDesktop, isMetric, fullLocale},
        viewport: {breakpoint},
        abacus
    }: ReduxState,
    {listing: {listingId}}: OwnProps
) => {
    const isHovered = listingId === hoveredListingId && isDesktop;
    return {
        brand,
        isDatelessSearch: isDateless(params),
        isDesktop,
        isHovered,
        isMetric,
        isFreeCancellationV2:
            (abacus[HA_SERP_FREE_CANCELLATION_V2] || {}).value === 2 || false,
        showDesktopHits: media(breakpoint, {
            minWidth: 'SM_MIN'
        }),
        fullLocale
    };
};

export default compose(
    injectIntl,
    connect(mapStateToProps)
)(HitInfo);
